import {
  AutoPlacementRuleConfigurationSchema,
  BaseFormEngineRuleConfiguration,
  BaseFormEngineRuleConfigurationSchema,
  RawFormEngineRule,
} from "src/types/formEngineRule";
import * as GQL from "src/types/graphql";

function parseAutoPlacementConfig(ruleConfig: string) {
  try {
    return AutoPlacementRuleConfigurationSchema.safeParse({
      ...JSON.parse(ruleConfig),
      rule_type: GQL.form_engine_rule_type_enum.AUTO_PLACEMENT,
    });
  } catch {
    return null;
  }
}

export function unavailableAutoPlacementSchoolIds(
  rules: RawFormEngineRule[],
  schools: { id: string }[],
  editingRuleId?: string
): string[] {
  const occupied = new Set<string>();

  for (const rule of rules) {
    if (
      rule.rule_type !== GQL.form_engine_rule_type_enum.AUTO_PLACEMENT ||
      rule.id === editingRuleId
    ) {
      continue;
    }

    const config = parseAutoPlacementConfig(rule.rule_config);
    if (!config?.success) {
      continue;
    }

    if (config.data.school_ids.length === 0) {
      return schools.map((school) => school.id);
    }

    for (const schoolId of config.data.school_ids) {
      occupied.add(schoolId);
    }
  }

  return [...occupied];
}

export class RuleInfo {
  public readonly name: string;
  public readonly description: string;
  public readonly tag_ids: string[];
  public readonly enable_automatic_offers: boolean = false;
  public readonly offer_from_waitlist_only: boolean = false;
  constructor(rule: RawFormEngineRule) {
    // AUTO_WAITLIST rules don't have tag_ids or enable_remove_tag
    // Parse them separately
    if (rule.rule_type === GQL.form_engine_rule_type_enum.AUTO_WAITLIST) {
      const rawConfig = JSON.parse(rule.rule_config);
      this.name = rawConfig.rule_name ?? "";
      this.description =
        rawConfig.rule_description ??
        "Automatically create waitlists based on form status";
      this.tag_ids = [];
      this.enable_automatic_offers = rawConfig.enable_automatic_offers ?? false;
      this.offer_from_waitlist_only =
        rawConfig.offer_from_waitlist_only ?? false;
      return;
    }

    if (rule.rule_type === GQL.form_engine_rule_type_enum.AUTO_PLACEMENT) {
      const config = parseAutoPlacementConfig(rule.rule_config);
      if (!config?.success) {
        this.name = "Invalid rule configuration";
        this.description = "Invalid rule configuration";
        this.tag_ids = [];
        return;
      }
      this.name = config.data.rule_name ?? "";
      this.description =
        config.data.rule_description ??
        "Automatically place students at their top ranked school upon form submission";
      this.tag_ids = config.data.tag_ids;
      return;
    }

    // For tag-based rules, use BaseFormEngineRuleConfigurationSchema
    const config = BaseFormEngineRuleConfigurationSchema.safeParse(
      JSON.parse(rule.rule_config)
    );
    if (!config.success || !config.data) {
      this.name = "Invalid rule configuration";
      this.description = "Invalid rule configuration";
      this.tag_ids = [];
    } else {
      this.name = config.data.rule_name ?? "";
      this.description = RuleInfo.makeDescription(rule, config.data);
      this.tag_ids = config.data.tag_ids ?? [];
    }
  }

  private static readonly makeDescription = (
    rule: RawFormEngineRule,
    config: BaseFormEngineRuleConfiguration
  ) => {
    if (config.rule_description) {
      return config.rule_description;
    } else {
      switch (rule.rule_type) {
        case GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG: {
          return "Apply tag when there's at least one sibling";
        }
        case GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG:
        case GQL.form_engine_rule_type_enum.QUESTION_TAG: {
          return "Apply tag based on student answer";
        }
        case GQL.form_engine_rule_type_enum.AUTO_WAITLIST: {
          return "Automatically create waitlists based on form status";
        }
        default: {
          return "Apply tag";
        }
      }
    }
  };
}
